import React, { useState } from 'react';
import {
    Activity,
    RefreshCw,
    ToggleLeft,
    ToggleRight,
    AlertTriangle,
    CheckCircle,
    Clock,
    TrendingUp,
    TrendingDown,
    Zap
} from 'lucide-react';
import { useLiveDataControls, toggleLotStatus, updateMetrics, useVersionInfo, forceVersionUpdate } from '../../lib/api';
import { LiveDataControl } from '../../lib/types';

// Lot ID to Name mapping
const LOT_NAME_MAPPING: Record<string, string> = {
    'aal_447': 'Gaasepigen',
    'aal_65': 'Kongrescenter area',
    'aal_67': 'Kongrescenter area',
    'aal_63': 'Kongrescenter area',
    'aal_62': 'Kongrescenter area',
    'aal_290': 'Kongrescenter area',
    'aal_370': 'Kongrescenter area',
    'aal_66': 'Kongrescenter area',
    'aal_64': 'Kongrescenter area',
    'aal_61': 'Kongrescenter area',
    'aal_104': 'Budolfi Plads',
    'aal_360': 'Sauers Plads',
    'aal_354': 'Sømandshjemmet',
    'aal_1396': 'Sømandshjemmet',
    'aal_362': 'C W Obel',
    'aal_128': 'Østre Havn P-Hus 2',
    'aal_548': 'Palads',
    'aal_355': 'Salling',
    'aal_356': 'Føtex',
    'aal_1106': 'Friis',
    'aal_1105': 'Musikkens Hus',
    'aal_129': 'Østre Havn',
    'aal_969': 'Kennedy Arkaden',
    'aal_822': 'Plaza P-Hus Nord',
    'aal_823': 'Plaza P-Hus Syd'
};

const LiveDataControls: React.FC = () => {
    const { controls, summary, isLoading, error, mutate, lastUpdated } = useLiveDataControls();
    const { versionInfo, isLoading: versionLoading, error: versionError, mutate: mutateVersion } = useVersionInfo();
    const [togglingLots, setTogglingLots] = useState<Set<string>>(new Set());
    const [updatingMetrics, setUpdatingMetrics] = useState(false);
    const [updatingVersion, setUpdatingVersion] = useState(false);
    const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

    // Calculate additional statistics
    const staticLots = controls.filter(control => control.occupancyChangesLastHour === 0).length;
    const activeLots = controls.filter(control => control.occupancyChangesLastHour > 0).length;

    const handleToggleLot = async (lotId: string) => {
        setTogglingLots(prev => new Set(prev).add(lotId));

        try {
            const response = await toggleLotStatus(lotId);
            setNotification({
                type: 'success',
                message: response.data.message
            });
            // Refresh data after successful toggle
            mutate();
        } catch (err) {
            setNotification({
                type: 'error',
                message: `Failed to toggle lot ${lotId}: ${err instanceof Error ? err.message : 'Unknown error'}`
            });
        } finally {
            setTogglingLots(prev => {
                const newSet = new Set(prev);
                newSet.delete(lotId);
                return newSet;
            });
        }
    };

    const handleRefreshMetrics = async () => {
        setUpdatingMetrics(true);

        try {
            const response = await updateMetrics();
            setNotification({
                type: 'success',
                message: response.data.message
            });
            // Refresh data after successful update
            mutate();
        } catch (err) {
            setNotification({
                type: 'error',
                message: `Failed to update metrics: ${err instanceof Error ? err.message : 'Unknown error'}`
            });
        } finally {
            setUpdatingMetrics(false);
        }
    };

    const handleVersionUpdate = async () => {
        setUpdatingVersion(true);

        try {
            const response = await forceVersionUpdate();
            setNotification({
                type: 'success',
                message: `Version updated to ${response.data.newVersion}. ${response.data.message}`
            });

            // Optimistically update the version info immediately
            mutateVersion((currentData) => {
                if (currentData?.data) {
                    return {
                        ...currentData,
                        data: {
                            ...currentData.data,
                            version: response.data.newVersion,
                            lastModified: new Date().toISOString(),
                            age: { days: 0, hours: 0, minutes: 0 }
                        }
                    };
                }
                return currentData;
            }, { revalidate: false });

            // Also force a background refresh to get the complete updated data
            setTimeout(() => {
                mutateVersion(undefined, { revalidate: true });
            }, 1000);
        } catch (err) {
            setNotification({
                type: 'error',
                message: `Failed to update version: ${err instanceof Error ? err.message : 'Unknown error'}`
            });
        } finally {
            setUpdatingVersion(false);
        }
    };

    const formatTimeAgo = (timestamp: string) => {
        const now = new Date();
        const time = new Date(timestamp);
        const diffMs = now.getTime() - time.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMins / 60);
        const diffDays = Math.floor(diffHours / 24);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        return `${diffDays}d ago`;
    };

    const getStatusColor = (control: LiveDataControl) => {
        if (!control.isEnabled) return 'text-red-600 bg-red-50';
        if (control.occupancyChangesLastHour === 0) return 'text-yellow-600 bg-yellow-50';
        return 'text-green-600 bg-green-50';
    };

    const getStatusIcon = (control: LiveDataControl) => {
        if (!control.isEnabled) return <AlertTriangle className="h-4 w-4" />;
        if (control.occupancyChangesLastHour === 0) return <AlertTriangle className="h-4 w-4" />;
        return <CheckCircle className="h-4 w-4" />;
    };

    const getStatusText = (control: LiveDataControl) => {
        if (!control.isEnabled) return 'Disabled';
        if (control.occupancyChangesLastHour === 0) return 'Static';
        return 'Active';
    };

    // Clear notification after 5 seconds
    React.useEffect(() => {
        if (notification) {
            const timer = setTimeout(() => setNotification(null), 5000);
            return () => clearTimeout(timer);
        }
    }, [notification]);

    if (error) {
        return (
            <div className="min-h-screen bg-background">
                <div className="container mx-auto px-4 py-8">
                    <div className="bg-card border rounded-lg p-8">
                        <div className="text-center text-red-600">
                            <AlertTriangle className="h-12 w-12 mx-auto mb-4" />
                            <h2 className="text-xl font-semibold mb-2">Error Loading Live Data Controls</h2>
                            <p className="mb-4">{error.message}</p>
                            <button
                                onClick={() => mutate()}
                                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                            >
                                Retry
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background">
            <div className="container mx-auto px-4 py-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-3xl font-bold text-foreground mb-2">Live Data Controls</h1>
                            <p className="text-muted-foreground">Manage real-time parking data quality and sensor status</p>
                        </div>
                        <div className="flex items-center gap-4">
                            {lastUpdated && (
                                <div className="text-sm text-muted-foreground flex items-center gap-2">
                                    <Clock className="h-4 w-4" />
                                    Last updated: {formatTimeAgo(lastUpdated)}
                                </div>
                            )}
                            <button
                                onClick={handleRefreshMetrics}
                                disabled={updatingMetrics}
                                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <RefreshCw className={`h-4 w-4 ${updatingMetrics ? 'animate-spin' : ''}`} />
                                {updatingMetrics ? 'Updating...' : 'Refresh Metrics'}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Notification */}
                {notification && (
                    <div className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${notification.type === 'success'
                        ? 'bg-green-50 text-green-800 border border-green-200'
                        : 'bg-red-50 text-red-800 border border-red-200'
                        }`}>
                        {notification.type === 'success' ? (
                            <CheckCircle className="h-5 w-5" />
                        ) : (
                            <AlertTriangle className="h-5 w-5" />
                        )}
                        <span>{notification.message}</span>
                        <button
                            onClick={() => setNotification(null)}
                            className="ml-auto text-gray-400 hover:text-gray-600"
                        >
                            ×
                        </button>
                    </div>
                )}

                {/* Summary Statistics */}
                <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
                    <div className="bg-card border rounded-lg p-6">
                        <div className="flex items-center gap-3">
                            <Activity className="h-8 w-8 text-blue-600" />
                            <div>
                                <p className="text-sm font-medium text-muted-foreground">Total Lots</p>
                                <p className="text-2xl font-bold text-foreground">{summary.total}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-card border rounded-lg p-6">
                        <div className="flex items-center gap-3">
                            <CheckCircle className="h-8 w-8 text-green-600" />
                            <div>
                                <p className="text-sm font-medium text-muted-foreground">Enabled Lots</p>
                                <p className="text-2xl font-bold text-foreground">{summary.enabled}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-card border rounded-lg p-6">
                        <div className="flex items-center gap-3">
                            <AlertTriangle className="h-8 w-8 text-red-600" />
                            <div>
                                <p className="text-sm font-medium text-muted-foreground">Disabled Lots</p>
                                <p className="text-2xl font-bold text-foreground">{summary.disabled}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-card border rounded-lg p-6">
                        <div className="flex items-center gap-3">
                            <TrendingDown className="h-8 w-8 text-yellow-600" />
                            <div>
                                <p className="text-sm font-medium text-muted-foreground">Static Lots</p>
                                <p className="text-2xl font-bold text-foreground">{staticLots}</p>
                                <p className="text-xs text-muted-foreground">0 changes/hour</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-card border rounded-lg p-6">
                        <div className="flex items-center gap-3">
                            <TrendingUp className="h-8 w-8 text-green-600" />
                            <div>
                                <p className="text-sm font-medium text-muted-foreground">Active Lots</p>
                                <p className="text-2xl font-bold text-foreground">{activeLots}</p>
                                <p className="text-xs text-muted-foreground">&gt;0 changes/hour</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Version Control Section */}
                <div className="bg-card border rounded-lg p-6 mb-8">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-blue-100 rounded-lg">
                                <RefreshCw className="h-6 w-6 text-blue-600" />
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold text-foreground">Version Control</h3>
                                <p className="text-sm text-muted-foreground">Manage live data system version</p>
                            </div>
                        </div>
                        <button
                            onClick={handleVersionUpdate}
                            disabled={updatingVersion}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <RefreshCw className={`h-4 w-4 ${updatingVersion ? 'animate-spin' : ''}`} />
                            {updatingVersion ? 'Updating...' : 'Update Version'}
                        </button>
                    </div>

                    {versionLoading ? (
                        <div className="flex items-center gap-3 text-muted-foreground">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                            <span className="text-sm">Loading version information...</span>
                        </div>
                    ) : versionError ? (
                        <div className="flex items-center gap-3 text-red-600">
                            <AlertTriangle className="h-4 w-4" />
                            <span className="text-sm">Failed to load version information: {versionError.message}</span>
                        </div>
                    ) : versionInfo ? (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="bg-muted/50 rounded-lg p-4">
                                <div className="text-sm text-muted-foreground mb-1">Current Version</div>
                                <div className="text-lg font-mono font-semibold text-foreground">
                                    {versionInfo.version}
                                </div>
                            </div>
                            <div className="bg-muted/50 rounded-lg p-4">
                                <div className="text-sm text-muted-foreground mb-1">Version Type</div>
                                <div className="text-lg font-semibold text-foreground">
                                    v1
                                </div>
                            </div>
                            <div className="bg-muted/50 rounded-lg p-4">
                                <div className="text-sm text-muted-foreground mb-1">Last Modified</div>
                                <div className="text-sm text-foreground">
                                    {formatTimeAgo(versionInfo.lastModified)}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                    {versionInfo.age.days > 0 && `${versionInfo.age.days}d `}
                                    {versionInfo.age.hours > 0 && `${versionInfo.age.hours}h `}
                                    {versionInfo.age.minutes}m ago
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="text-center text-muted-foreground py-4">
                            <span className="text-sm">No version information available</span>
                        </div>
                    )}
                </div>

                {/* Controls Table */}
                <div className="bg-card border rounded-lg">
                    <div className="p-6 border-b">
                        <h2 className="text-xl font-semibold text-foreground">Parking Lot Controls</h2>
                        <p className="text-sm text-muted-foreground mt-1">
                            Monitor and control live data feeds for each parking lot
                        </p>
                    </div>

                    {isLoading ? (
                        <div className="p-8 text-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
                            <p className="mt-2 text-sm text-muted-foreground">Loading live data controls...</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-muted/50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                            Lot ID & Name
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                            Status
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                            Last Updated
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                            Changes/Hour
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                            Max Change
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                            Data Points
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                            Action
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-border">
                                    {controls.map((control) => (
                                        <tr key={control.lotId} className="hover:bg-muted/25">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center gap-2">
                                                    <Zap className="h-4 w-4 text-blue-600" />
                                                    <div>
                                                        <div className="text-sm font-medium text-foreground">
                                                            {control.lotId}
                                                        </div>
                                                        <div className="text-xs text-muted-foreground">
                                                            {LOT_NAME_MAPPING[control.lotId] || 'Unknown Location'}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`inline-flex items-center gap-2 px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(control)}`}>
                                                    {getStatusIcon(control)}
                                                    {getStatusText(control)}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                                                {formatTimeAgo(control.lastUpdated)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                                                <span className={`font-medium ${control.occupancyChangesLastHour === 0
                                                    ? 'text-yellow-600'
                                                    : control.occupancyChangesLastHour > 10
                                                        ? 'text-green-600'
                                                        : 'text-blue-600'
                                                    }`}>
                                                    {control.occupancyChangesLastHour}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                                                <span className={`font-medium ${control.maxChangeLastHour > 50
                                                    ? 'text-red-600'
                                                    : control.maxChangeLastHour > 20
                                                        ? 'text-yellow-600'
                                                        : 'text-green-600'
                                                    }`}>
                                                    {control.maxChangeLastHour}%
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                                                {control.dataPointsLastHour}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <button
                                                    onClick={() => handleToggleLot(control.lotId)}
                                                    disabled={togglingLots.has(control.lotId)}
                                                    className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${control.isEnabled
                                                        ? 'bg-red-100 text-red-700 hover:bg-red-200'
                                                        : 'bg-green-100 text-green-700 hover:bg-green-200'
                                                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                                                >
                                                    {togglingLots.has(control.lotId) ? (
                                                        <RefreshCw className="h-4 w-4 animate-spin" />
                                                    ) : control.isEnabled ? (
                                                        <ToggleLeft className="h-4 w-4" />
                                                    ) : (
                                                        <ToggleRight className="h-4 w-4" />
                                                    )}
                                                    {togglingLots.has(control.lotId)
                                                        ? 'Toggling...'
                                                        : control.isEnabled
                                                            ? 'Disable'
                                                            : 'Enable'
                                                    }
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Data Interpretation Guide */}
                <div className="mt-8 bg-card border rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-foreground mb-4">Data Interpretation Guide</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <h4 className="font-medium text-foreground mb-2">Status Indicators</h4>
                            <ul className="space-y-2 text-sm text-muted-foreground">
                                <li className="flex items-center gap-2">
                                    <CheckCircle className="h-4 w-4 text-green-600" />
                                    <span><strong>Active:</strong> Working sensors with data changes</span>
                                </li>
                                <li className="flex items-center gap-2">
                                    <AlertTriangle className="h-4 w-4 text-yellow-600" />
                                    <span><strong>Static:</strong> Likely broken sensors (0 changes/hour)</span>
                                </li>
                                <li className="flex items-center gap-2">
                                    <AlertTriangle className="h-4 w-4 text-red-600" />
                                    <span><strong>Disabled:</strong> Manually disabled by admin</span>
                                </li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="font-medium text-foreground mb-2">Metrics Guide</h4>
                            <ul className="space-y-2 text-sm text-muted-foreground">
                                <li><strong>Changes/Hour:</strong> Occupancy changes in last hour</li>
                                <li><strong>Max Change:</strong> Biggest single change (potential sensor issues if &gt;50%)</li>
                                <li><strong>Data Points:</strong> Number of data updates in last hour</li>
                                <li><strong>Recent Updates:</strong> Data freshness indicator</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LiveDataControls;
